import React, { useEffect, useState } from "react";
import staffApi from "@/lib/staffApi";
import { PageHead } from "./DashboardLayout";
import { useStaff, roleLabel } from "@/context/StaffContext";
import { money } from "@/data/site";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";

export function Waitlist() {
  const [rows, setRows] = useState([]);
  useEffect(() => { staffApi.get("/admin/waitlist").then((r) => setRows(r.data)); }, []);
  const exportCsv = async () => {
    try {
      const res = await staffApi.get("/admin/waitlist/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "waitlist.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };
  return (
    <div data-testid="waitlist-page">
      <PageHead kicker="Demand" title="Relaunch Waitlist"
        action={<button onClick={exportCsv} disabled={rows.length === 0} data-testid="waitlist-export" className="flex items-center gap-2 border border-white/15 px-5 py-3 text-xs uppercase tracking-widest text-cream hover:border-gold hover:text-gold disabled:opacity-40"><Download size={14} /> Export CSV</button>} />
      <p className="mb-6 text-sm text-cream/50">{rows.length} {rows.length === 1 ? "person is" : "people are"} waiting for checkout to relaunch.</p>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[420px] text-sm" data-testid="waitlist-table">
          <thead><tr className="bg-ink text-gold/80">{["Email", "Joined"].map((h) => <th key={h} className="px-4 py-3 text-left font-mono text-[0.65rem] uppercase tracking-widest">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={2} className="px-4 py-10 text-center text-cream/50">No signups yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.email} className="border-t border-white/10 text-cream/80" data-testid={`waitlist-row-${r.email}`}>
                <td className="px-4 py-3 text-cream">{r.email}</td>
                <td className="px-4 py-3 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Members() {
  const [members, setMembers] = useState([]);
  useEffect(() => { staffApi.get("/admin/members").then((r) => setMembers(r.data)); }, []);
  return (
    <div data-testid="members-page">
      <PageHead kicker="Community" title="Member Directory" />
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[560px] text-sm" data-testid="members-table">
          <thead><tr className="bg-ink text-gold/80">{["Name", "Email", "Orders", "Total Spent", "Joined"].map((h) => <th key={h} className="px-4 py-3 text-left font-mono text-[0.65rem] uppercase tracking-widest">{h}</th>)}</tr></thead>
          <tbody>
            {members.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-cream/50">No members yet.</td></tr>}
            {members.map((m) => (
              <tr key={m.id} className="border-t border-white/10 text-cream/80" data-testid={`member-row-${m.id}`}>
                <td className="px-4 py-3 text-cream">{m.name}</td>
                <td className="px-4 py-3">{m.email}</td>
                <td className="px-4 py-3 font-mono">{m.order_count}</td>
                <td className="px-4 py-3 font-mono text-gold">{money(m.total_spent)}</td>
                <td className="px-4 py-3 text-xs">{new Date(m.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Locations() {
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ name: "", code: "", address: "" });
  const fetchData = () => staffApi.get("/admin/locations").then((r) => setLocations(r.data));
  useEffect(() => { fetchData(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await staffApi.post("/admin/locations", form); toast.success("Location added"); setForm({ name: "", code: "", address: "" }); fetchData(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="locations-page">
      <PageHead kicker="Network" title="Inventory Locations" />
      <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          {locations.map((l) => (
            <div key={l.id} className="border border-white/10 bg-ink p-5" data-testid={`location-${l.code}`}>
              <div className="flex items-center justify-between"><p className="font-serif text-lg text-cream">{l.name}</p><span className="font-mono text-xs text-gold">{l.code}</span></div>
              <p className="mt-1 text-sm text-cream/50">{l.address}</p>
            </div>
          ))}
        </div>
        <form onSubmit={submit} className="h-fit border border-gold/25 bg-ink p-5" data-testid="location-form">
          <h3 className="mb-4 font-serif text-lg text-cream">Add Location</h3>
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testid="loc-name" />
          <Input label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} testid="loc-code" />
          <Input label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required={false} testid="loc-address" />
          <button data-testid="loc-submit" className="w-full bg-gold py-3 text-xs font-bold uppercase tracking-widest text-black hover:bg-gold-bright">Add Location</button>
        </form>
      </div>
    </div>
  );
}

export function StaffMgmt() {
  const { staff: me } = useStaff();
  const [staff, setStaff] = useState([]);
  const fetchData = () => staffApi.get("/admin/staff").then((r) => setStaff(r.data));
  useEffect(() => { fetchData(); }, []);
  const setRole = async (id, role) => {
    try { await staffApi.put(`/admin/staff/${id}/role`, { role }); toast.success("Role updated"); fetchData(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="staff-page">
      <PageHead kicker="Access Control" title="Staff Management" />
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[560px] text-sm" data-testid="staff-table">
          <thead><tr className="bg-ink text-gold/80">{["Name", "Email", "Role", "Set Role"].map((h) => <th key={h} className="px-4 py-3 text-left font-mono text-[0.65rem] uppercase tracking-widest">{h}</th>)}</tr></thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className="border-t border-white/10 text-cream/80" data-testid={`staff-row-${s.id}`}>
                <td className="px-4 py-3 text-cream">{s.name}</td>
                <td className="px-4 py-3">{s.email}</td>
                <td className="px-4 py-3 text-gold">{roleLabel[s.role]}</td>
                <td className="px-4 py-3">
                  <select value={s.role} disabled={s.id === me.id} onChange={(e) => setRole(s.id, e.target.value)} data-testid={`role-select-${s.id}`} className="border border-white/15 bg-black px-3 py-1.5 text-xs text-cream outline-none focus:border-gold disabled:opacity-40">
                    {["junior", "admin", "super"].map((r) => <option key={r} value={r} className="bg-black">{roleLabel[r]}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AuditLog() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { staffApi.get("/admin/audit-logs").then((r) => setLogs(r.data)); }, []);
  return (
    <div data-testid="audit-page">
      <PageHead kicker="Security" title="Audit Log" />
      <div className="border border-white/10" data-testid="audit-list">
        {logs.length === 0 && <p className="p-8 text-center text-sm text-cream/50">No activity logged yet.</p>}
        {logs.map((l) => (
          <div key={l.id} className="flex items-start justify-between border-b border-white/10 px-5 py-4 last:border-0">
            <div>
              <p className="text-sm text-cream"><span className="font-mono text-gold">{l.action}</span> — {l.detail}</p>
              <p className="mt-0.5 text-xs text-cream/40">{l.staff_name}</p>
            </div>
            <span className="text-xs text-cream/40">{new Date(l.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EmailLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { staffApi.get("/admin/email-log").then((r) => setRows(r.data)); }, []);
  const badge = {
    delivered: "border-green-500/40 text-green-400",
    blocked: "border-yellow-500/40 text-yellow-400",
    failed: "border-red-500/40 text-red-400",
  };
  const kindLabel = { digest: "Low-Stock Digest", "digest-test": "Digest (test)", weekly: "Weekly Recap",
    shipping: "Shipping", "shipping-test": "Shipping (test)" };
  return (
    <div data-testid="email-log-page">
      <PageHead kicker="Deliverability" title="Email Log" />
      <p className="mb-6 text-sm text-cream/50">Recent outbound emails (digests, shipping, weekly recap) with delivery status.</p>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[720px] text-sm" data-testid="email-log-table">
          <thead><tr className="bg-ink text-gold/80">{["Type", "Recipient", "Subject", "Status", "Sent"].map((h) => <th key={h} className="px-4 py-3 text-left font-mono text-[0.65rem] uppercase tracking-widest">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-cream/50">No emails sent yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/10 text-cream/80" data-testid={`email-log-row-${r.id}`}>
                <td className="px-4 py-3 text-xs text-cream/70">{kindLabel[r.kind] || r.kind}</td>
                <td className="px-4 py-3">{r.to}</td>
                <td className="px-4 py-3 text-xs text-cream/60 max-w-[280px] truncate">{r.subject}</td>
                <td className="px-4 py-3"><span className={`inline-flex border px-2 py-1 text-[0.6rem] uppercase tracking-widest capitalize ${badge[r.status] || "border-white/20 text-cream/60"}`} data-testid={`email-status-${r.id}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, required = true, testid }) {
  return <label className="mb-3 block"><span className="overline-label mb-1 block text-[0.6rem]">{label}</span>
    <input required={required} value={value} onChange={onChange} data-testid={testid} className="w-full border border-white/15 bg-black px-4 py-2 text-sm text-cream outline-none focus:border-gold" /></label>;
}


const STATUS = {
  active: { label: "Active now", dot: "bg-green-400", ring: "ring-green-400/40", text: "text-green-400" },
  recently: { label: "Recently active", dot: "bg-yellow-400", ring: "ring-yellow-400/40", text: "text-yellow-400" },
  offline: { label: "Offline", dot: "bg-mutedgray", ring: "ring-white/10", text: "text-mutedgray" },
  never: { label: "Never signed in", dot: "bg-mutedgray/50", ring: "ring-white/5", text: "text-mutedgray" },
};

const relTime = (iso) => {
  if (!iso) return "Never";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const ACTION_LABEL = {
  stock_adjust: "Adjusted stock", product_create: "Created product", product_update: "Edited product",
  product_archive: "Archived product", order_status: "Updated order", location_create: "Added location",
  staff_role: "Changed staff role",
};

export function StaffActivity() {
  const { staff: me } = useStaff();
  const [data, setData] = useState({ scope: "all", staff: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    staffApi.get("/admin/staff-activity").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="staff-activity-page">
      <PageHead kicker="Team" title="Staff Activity" />
      <div className="mb-6 flex flex-wrap items-center gap-4 text-xs text-cream/60">
        {Object.entries(STATUS).slice(0, 3).map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${s.dot}`} /> {s.label}</span>
        ))}
        {data.scope === "junior" && <span className="ml-auto text-gold/70">You can view junior staff activity only</span>}
      </div>

      {loading ? (
        <p className="text-cream/50">Loading activity…</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {data.staff.map((s) => {
            const st = STATUS[s.status] || STATUS.never;
            return (
              <div key={s.id} className="border border-white/10 bg-ink p-5" data-testid={`activity-card-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="flex items-center gap-2 text-cream">
                      {s.name}
                      {s.is_self && <span className="border border-gold/40 px-1.5 py-0.5 text-[0.55rem] uppercase tracking-widest text-gold">You</span>}
                    </p>
                    <p className="text-xs text-cream/40">{s.email}</p>
                  </div>
                  <span className="text-[0.6rem] uppercase tracking-widest text-gold">{roleLabel[s.role]}</span>
                </div>

                <div className="mt-3 flex items-center justify-between border-y border-white/10 py-2.5">
                  <span className={`inline-flex items-center gap-2 text-xs ${st.text}`} data-testid={`activity-status-${s.id}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${st.dot} ring-4 ${st.ring}`} /> {st.label}
                  </span>
                  <span className="text-xs text-cream/40">Last active {relTime(s.last_active)}</span>
                </div>

                <p className="mb-2 mt-3 overline-label text-[0.6rem]">Recent activity · {s.activity_count} total</p>
                {s.recent_activities.length === 0 ? (
                  <p className="text-xs text-cream/40">No activity logged yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {s.recent_activities.map((a) => (
                      <li key={a.id} className="flex items-start justify-between gap-3 text-xs">
                        <span className="text-cream/80"><span className="text-gold">{ACTION_LABEL[a.action] || a.action}</span>{a.detail ? ` — ${a.detail}` : ""}</span>
                        <span className="shrink-0 text-cream/40">{relTime(a.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
